import { useCallback, useEffect, useRef, useState } from "react";
import { confirmDialog, Dialog } from "../../components/Dialog";
import { Button, ErrorText, inputCls, Panel, timeAgo } from "../../components/ui";
import { del, get, post } from "../../lib/api";

/** The Google account's status (AIT-365): its kept session, and the latest word on whether it is still signed in. */
export type GoogleStatus = {
  saved: boolean;
  savedAt: string | null;
  available: boolean;
  signedIn: { ok: boolean; at: string; source: "check" | "meeting" | "join" | "signin"; detail: string | null } | null;
  checkedAt: string | null;
};
type Status = GoogleStatus;

/** The account has a kept session and the latest word says it is no longer signed in: Meet joins fail until it is signed in again. */
export const signedOut = (s: GoogleStatus | null) => Boolean(s?.saved && s.signedIn && !s.signedIn.ok);

const SEEN_BY: Record<NonNullable<GoogleStatus["signedIn"]>["source"], string> = {
  check: "the daily check", meeting: "a meeting", join: "a join", signin: "the sign-in",
};

/**
 * Admin → Meetings, on every tab (AIT-365): the Pandas Google account is no longer signed in, so Meet joins fail until it
 * is signed in again. Absent while it is signed in.
 */
export function GoogleSignInBanner({ status, onSignIn }: { status: GoogleStatus | null; onSignIn: () => void }) {
  if (!signedOut(status)) return null;
  const s = status!.signedIn!;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md bg-warning/10 px-3 py-2 text-sm text-warning">
      <span className="min-w-0 flex-1">
        <b className="font-medium">Google needs the Pandas account signed in again.</b> Meet joins fail until it is. Seen by {SEEN_BY[s.source]} {timeAgo(s.at)}
        {s.detail ? <span className="mt-0.5 block text-xs break-words">Google's page said: {s.detail}</span> : null}
      </span>
      <Button size="sm" onClick={onSignIn}>Sign in again</Button>
    </div>
  );
}

const SHOT_EVERY_MS = 900;

/**
 * Admin → Meetings → Settings: the Google account Pandas joins Google Meet as. Its session is kept encrypted by the platform and
 * goes to the bot with every join, so renewing it is done here and restarts nothing. Signing in is done by hand in a browser the
 * bot runs: this dialog shows a picture of it and relays clicks and typing.
 */
export function GoogleAccountPanel() {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => get<Status>("/api/meetings/google-account").then(setStatus).catch((e: Error) => setError(e.message)), []);
  async function check() {
    setChecking(true);
    setError(null);
    try {
      setStatus(await post<Status>("/api/meetings/google-account/check"));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setChecking(false);
    }
  }
  useEffect(() => void load(), [load]);

  async function remove() {
    if (!(await confirmDialog({ title: "Remove the saved Google session?", body: "Pandas can no longer join Meet signed in until you sign in again here. The bot's own variable, if it has one, is used instead.", confirm: "Remove session", danger: true }))) return;
    setError(null);
    try {
      setStatus(await del<Status>("/api/meetings/google-account"));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Panel
      title="Google account"
      hint="The account Pandas joins Google Meet as."
      action={
        status && (
          <div className="flex gap-2">
            {status.saved && <Button size="sm" variant="danger" onClick={remove}>Remove</Button>}
            {status.saved && <Button size="sm" disabled={!status.available || checking} onClick={check}>{checking ? "Checking…" : "Check now"}</Button>}
            <Button size="sm" disabled={!status.available} onClick={() => setSigning(true)}>{status.saved ? "Sign in again" : "Sign in"}</Button>
          </div>
        )
      }
    >
      <ErrorText error={error} />
      {status && (
        <p className="text-sm">
          {status.saved ? (signedOut(status) ? <span className="text-warning">Signed out: Google needs the account signed in again.</span> : `Signed in, kept ${timeAgo(status.savedAt)}.`) : "No session kept here."}
          {status.saved && (
            <span className="mt-1 block text-xs text-muted">
              {status.checkedAt ? `Checked ${timeAgo(status.checkedAt)}` : "Not checked yet"}; checked every day, and the session is kept fresh after every Meet call.
            </span>
          )}
          <span className="mt-1 block text-xs text-muted">
            {!status.available
              ? "The meeting bot service is not configured."
              : status.saved
                ? "It goes to the bot with every join, so signing in again never restarts the bot. If Google asks the account to verify itself, sign in again."
                : "The bot uses its own GOOGLE_STORAGE_STATE variable if it has one, and joins as a guest otherwise."}
          </span>
        </p>
      )}
      {signing && <SignInDialog onClose={() => setSigning(false)} onSaved={load} />}
    </Panel>
  );
}

function SignInDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [id, setId] = useState<string | null>(null);
  const [picture, setPicture] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [text, setText] = useState("");
  const idRef = useRef<string | null>(null);
  const kick = useRef<() => void>(() => {});

  // Open the bot's browser when the dialog opens; close it when the dialog closes without finishing.
  useEffect(() => {
    let alive = true;
    post<{ id: string }>("/api/meetings/google-account/signin")
      .then((s) => {
        if (!alive) return void post(`/api/meetings/google-account/signin/${s.id}/cancel`).catch(() => {});
        idRef.current = s.id;
        setId(s.id);
      })
      .catch((e: Error) => alive && setError(e.message));
    return () => {
      alive = false;
      if (idRef.current) void post(`/api/meetings/google-account/signin/${idRef.current}/cancel`).catch(() => {});
    };
  }, []);

  // Picture and sign-in progress, one after the other, only while the tab is showing.
  useEffect(() => {
    if (!id || done) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let last: string | null = null;
    const base = `/api/meetings/google-account/signin/${id}`;
    async function tick() {
      timer = undefined;
      if (!document.hidden) {
        try {
          const res = await fetch(`${base}/shot`, { credentials: "same-origin", cache: "no-store" });
          if (!res.ok) throw new Error(res.status === 404 ? "The sign-in browser closed. Close this and start again." : `The picture failed (HTTP ${res.status})`);
          const url = URL.createObjectURL(await res.blob());
          if (!alive) return void URL.revokeObjectURL(url);
          if (last) URL.revokeObjectURL(last);
          last = url;
          setPicture(url);
          const s = await get<{ active: boolean; done: boolean }>(`${base}/status`);
          if (!alive) return;
          if (s.done) {
            setDone(true);
            onSaved();
            return;
          }
          if (!s.active) throw new Error("The sign-in browser closed. Close this and start again.");
          setError(null);
        } catch (e) {
          if (alive) setError((e as Error).message);
        }
      }
      if (alive) timer = setTimeout(tick, SHOT_EVERY_MS);
    }
    kick.current = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(tick, 250);
    };
    void tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      if (last) URL.revokeObjectURL(last);
    };
  }, [id, done, onSaved]);

  const send = useCallback(
    async (path: string, body: unknown) => {
      if (!id) return;
      try {
        await post(`/api/meetings/google-account/signin/${id}/${path}`, body);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      }
      kick.current();
    },
    [id],
  );

  function clickPicture(e: React.MouseEvent<HTMLImageElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    void send("click", { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title="Sign in to Google"
      description="You are driving a browser on the meeting bot. Click a field in the picture, type below and press Enter, and the session is kept as soon as Google accepts it."
      cancel={done ? false : "Cancel"}
      primary={done ? <Button variant="primary" onClick={onClose}>Done</Button> : undefined}
    >
      <div className="space-y-3">
        <ErrorText error={error} />
        {done ? (
          <p className="text-sm">Signed in. The session is saved and goes to the bot with every join.</p>
        ) : (
          <>
            <div className="overflow-hidden rounded-xl bg-surface ring-1 ring-line">
              {picture ? (
                <img src={picture} alt="The bot's browser, showing Google's sign-in page. Click to focus a field." className="block w-full cursor-pointer select-none" draggable={false} onClick={clickPicture} />
              ) : (
                <div className="flex aspect-[10/7] items-center justify-center text-sm text-muted">{error ? "" : "Opening Google…"}</div>
              )}
            </div>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void send("type", { text, enter: true });
                setText("");
              }}
            >
              <input className={inputCls} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type into the focused field: email, password, a code" aria-label="Text to type" autoComplete="off" data-autofocus />
              <Button type="submit" disabled={!id || !text}>Type and Enter</Button>
            </form>
            <div className="flex flex-wrap gap-2">
              {["Enter", "Tab", "Backspace"].map((key) => (
                <Button key={key} size="sm" disabled={!id} onClick={() => void send("key", { key })}>{key}</Button>
              ))}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
